import type { MessageEntityType } from "./telegram-types.js";

/**
 * Maximum length of a Telegram text message in UTF-16 code units.
 * Applies to `sendMessage` text field.
 *
 * @see https://core.telegram.org/bots/api#sendmessage
 */
export const MAX_MESSAGE_LENGTH = 4096 as const;

/**
 * Maximum length of a Telegram media caption in UTF-16 code units.
 * Applies to photo, video, document, and other media captions.
 *
 * @see https://core.telegram.org/bots/api#sendphoto
 */
export const MAX_CAPTION_LENGTH = 1024 as const;

/**
 * Maximum length of a Telegram media caption for Premium users in UTF-16 code units.
 * Premium users can send captions up to 4096 characters (same as message length).
 *
 * @see https://core.telegram.org/bots/api#sendphoto
 */
export const MAX_CAPTION_LENGTH_PREMIUM = 4096 as const;

/**
 * Default pagination limit for post history queries.
 */
export const DEFAULT_HISTORY_LIMIT = 50 as const;

/**
 * Maximum pagination limit for post history queries.
 * Prevents clients from requesting excessively large pages.
 */
export const MAX_HISTORY_LIMIT = 200 as const;

/**
 * All API route paths as constants.
 * Used by both client (to construct URLs) and server (to register routes).
 * All paths are relative to the server base URL.
 */
export const API_PATHS = {
  /** POST -- Verify JWT token validity. */
  AUTH_VERIFY: "/api/auth/verify",

  /** GET -- List all configured channels. */
  CHANNELS_LIST: "/api/channels",

  /** GET -- Get details for a specific channel. Append `/:id`. */
  CHANNELS_DETAIL: "/api/channels/:id",

  /** POST -- Send a post to a Telegram channel. */
  POSTS_SEND: "/api/posts/send",

  /** POST -- Send a media post to a Telegram channel (multipart/form-data). */
  POSTS_SEND_MEDIA: "/api/posts/send-media",

  /** POST -- Validate a post without sending. */
  POSTS_PREVIEW: "/api/posts/preview",

  /** GET -- Paginated post history. */
  POSTS_HISTORY: "/api/posts/history",

  /** GET -- Server health check. */
  HEALTH: "/api/health",

  /** POST -- Add a new channel (admin). */
  ADMIN_CHANNELS_CREATE: "/api/admin/channels",

  /** DELETE -- Remove a channel (admin). Append `/:id`. */
  ADMIN_CHANNELS_DELETE: "/api/admin/channels/:id",

  /** PUT -- Update a channel (admin). Append `/:id`. */
  ADMIN_CHANNELS_UPDATE: "/api/admin/channels/:id",
} as const;

/**
 * Type representing all API path values.
 * Useful for building generic HTTP client utilities.
 */
export type ApiPath = (typeof API_PATHS)[keyof typeof API_PATHS];

/**
 * MessageEntity types that support nesting (can contain other entities).
 * These types can appear as both parent and child in a formatting tree.
 */
export const NESTABLE_ENTITY_TYPES: readonly MessageEntityType[] = [
  "bold",
  "italic",
  "underline",
  "strikethrough",
  "spoiler",
] as const;

/**
 * MessageEntity types that CANNOT contain other entities.
 * Code blocks and inline code are rendered as-is without further formatting.
 */
export const NON_NESTABLE_ENTITY_TYPES: readonly MessageEntityType[] = [
  "code",
  "pre",
] as const;

/**
 * Blockquote entity types that cannot nest inside each other.
 */
export const BLOCKQUOTE_ENTITY_TYPES: readonly MessageEntityType[] = [
  "blockquote",
  "expandable_blockquote",
] as const;

// ---- Media Constants ----

/** Maximum photo file size in bytes (10 MB). */
export const MAX_PHOTO_SIZE_BYTES = 10 * 1024 * 1024;

/** Maximum video file size in bytes (50 MB). */
export const MAX_VIDEO_SIZE_BYTES = 50 * 1024 * 1024;

// ---- Telegram Premium Limits ----

/**
 * Telegram Premium limits summary.
 * Used for display purposes and limit enforcement when premium is enabled.
 */
export const TELEGRAM_PREMIUM_LIMITS = {
  /** Max file upload size for free users (2 GB). */
  maxFileUploadFree: 2 * 1024 * 1024 * 1024,
  /** Max file upload size for premium users (4 GB). */
  maxFileUploadPremium: 4 * 1024 * 1024 * 1024,
  /** Caption length for free users. */
  captionLengthFree: 1024,
  /** Caption length for premium users. */
  captionLengthPremium: 4096,
  /** Text message length (same for both). */
  messageLengthFree: 4096,
  messageLengthPremium: 4096,
  /** Followed channels limit. */
  followedChannelsFree: 500,
  followedChannelsPremium: 1000,
} as const;

/** Maximum number of items in a media group (album). */
export const MAX_MEDIA_GROUP_SIZE = 10;

/** Minimum number of items in a media group (album). */
export const MIN_MEDIA_GROUP_SIZE = 2;

/** MIME types accepted as photos. */
export const ALLOWED_PHOTO_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
] as const;

/** MIME types accepted as videos. */
export const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/quicktime",
] as const;

/** MIME types accepted as audio files. */
export const ALLOWED_AUDIO_TYPES = [
  "audio/mpeg",
  "audio/mp4",
  "audio/x-m4a",
  "audio/aac",
] as const;

/** Maximum audio file size in bytes (50 MB). */
export const MAX_AUDIO_SIZE_BYTES = 50 * 1024 * 1024;
